import React, {useEffect, useState, useContext} from "react";
import {Box, Stack, Typography, IconButton, Button, Modal, Fade, InputBase, Alert, CircularProgress} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Alert02Icon, Cancel01Icon, CheckIcon, Plug01Icon, LinkIcon} from "@hugeicons/core-free-icons";
import {Context} from "../../../../context/AppContext";

const controlSx = {height: 40, display: "flex", alignItems: "center", gap: 1, px: 1.25, borderRadius: "10px", bgcolor: "#FAFAFD", color: "#6B6B80", fontSize: 13};
const emptyServer = {name: "", endpoint: "", active: true, flow_uid: ""};
const isValidUrl = (url) => {try {new URL(url); return true;} catch {return false;}};

export const EditMCPServerModalNew = ({open, onClose, onSave, serverData}) => {
    const [server, setServer] = useState({...emptyServer});
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const {testMCPConnection} = useContext(Context);

    useEffect(() => {
        if (open && serverData) {
            setServer({name: serverData.name || "", endpoint: serverData.endpoint || "", active: serverData.active ?? true, flow_uid: serverData.flow_uid || ""});
            setTestResult(null);
        }
    }, [open, serverData]);

    const onChange = (key, value) => {
        setServer(prev => ({...prev, [key]: value}));
        if (key === "endpoint") setTestResult(null);
    };

    const handleTest = async () => {
        if (!server.endpoint) return;
        setTesting(true);
        const data = await testMCPConnection({endpoint: server.endpoint});
        setTestResult(data);
        setTesting(false);
    };

    const handleSave = async () => {
        if (!server.name || !server.endpoint) return;
        try {
            setSaving(true);
            await onSave(serverData.id, {name: server.name, endpoint: server.endpoint, active: server.active, flow_uid: server.flow_uid});
            onClose();
        } catch (e) {
            console.error("Failed to update MCP server:", e);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Fade in={open}>
                <Box sx={{position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: {xs: "calc(100% - 24px)", md: "min(520px, 92vw)"}, bgcolor: '#fff', borderRadius: '20px', boxShadow: '0 24px 70px rgba(35, 38, 77, 0.22)', p: {xs: 2.5, md: 3}, outline: 'none'}}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{mb: 3}}>
                        <Box>
                            <Typography sx={{fontSize: 18, fontWeight: 700, color: '#23264D', letterSpacing: '-0.01em'}}>Edit MCP Server</Typography>
                            <Typography sx={{fontSize: 13, color: '#8A8AA0', mt: 0.5}}>Update server configuration and settings</Typography>
                        </Box>
                        <IconButton size="small" onClick={onClose} sx={{color: '#8A8AA0'}}><HugeiconsIcon icon={Cancel01Icon} size={18} /></IconButton>
                    </Stack>

                    <Stack gap={2} sx={{mb: 2.5}}>
                        <Box>
                            <Typography sx={{fontSize: 12, fontWeight: 600, color: '#6B6B80', mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.05em'}}>Server Name</Typography>
                            <Box sx={{...controlSx}}>
                                <InputBase placeholder="e.g., My MCP Server" value={server.name} onChange={e => onChange("name", e.target.value)} inputProps={{"aria-label": "Server name"}} autoFocus sx={{flex: 1, fontSize: 13, color: "#23264D"}} />
                            </Box>
                        </Box>

                        <Box>
                            <Typography sx={{fontSize: 12, fontWeight: 600, color: '#6B6B80', mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.05em'}}>Endpoint URL</Typography>
                            <Box sx={{...controlSx, ...(!!server.endpoint && !isValidUrl(server.endpoint) && {borderColor: '#DC2626'})}}>
                                <HugeiconsIcon icon={LinkIcon} size={16} />
                                <InputBase placeholder="https://..." value={server.endpoint} onChange={e => onChange("endpoint", e.target.value)} inputProps={{"aria-label": "Endpoint URL"}} sx={{flex: 1, fontSize: 13, color: "#23264D"}} />
                            </Box>
                            {!!server.endpoint && !isValidUrl(server.endpoint) && (<Typography sx={{fontSize: 12, color: '#DC2626', mt: 0.5}}>Invalid URL format</Typography>)}
                        </Box>
                    </Stack>

                    <Box sx={{mb: 2.5}}>
                        <Button size="small" disabled={testing || !server.endpoint || !isValidUrl(server.endpoint)} startIcon={testing ? <CircularProgress size={14}/> : <HugeiconsIcon icon={Plug01Icon} size={16}/>} onClick={handleTest} sx={{textTransform: "none", borderRadius: '10px', fontWeight: 600, fontSize: 13, color: '#8491F7', '&:hover': {bgcolor: 'rgba(132, 145, 247, 0.08)'}}}>{testing ? "Testing..." : "Test Connection"}</Button>
                        {testResult && (<Alert severity={testResult.ok ? "success" : "error"} icon={testResult.ok ? <HugeiconsIcon icon={CheckIcon} size={18}/> : <HugeiconsIcon icon={Alert02Icon} size={18}/>} sx={{borderRadius: '10px', fontSize: 13, mt: 1.5, ...(testResult.ok ? {bgcolor: 'rgba(47, 154, 106, 0.08)', color: '#2F9A6A', border: '1px solid #2F9A6A'} : {bgcolor: 'rgba(220, 38, 38, 0.08)', color: '#DC2626', border: '1px solid #DC2626'})}}>{testResult.message}{testResult.ok && testResult.server_name && (<Box component="span" sx={{display: "block", fontSize: 12, opacity: 0.8, mt: 0.5}}>{testResult.server_name}{testResult.protocol_version && ` · MCP ${testResult.protocol_version}`}{testResult.tool_count != null && ` · ${testResult.tool_count} tools`}</Box>)}</Alert>)}
                    </Box>

                    <Stack direction="row" gap={1.5} justifyContent="flex-end">
                        <Button onClick={onClose} sx={{textTransform: 'none', fontWeight: 600, fontSize: 13, borderRadius: '10px', color: '#374151', bgcolor: '#FAFAFD', '&:hover': {bgcolor: '#ECECF3'}}}>Cancel</Button>
                        <Button disabled={!server.name || !server.endpoint || saving || !isValidUrl(server.endpoint)} onClick={handleSave} sx={{textTransform: 'none', fontWeight: 600, fontSize: 13, borderRadius: '10px', bgcolor: '#374151', color: '#fff', '&:hover': {bgcolor: '#1F2937'}, '&.Mui-disabled': {bgcolor: '#D1D5DB', color: '#9CA3AF'}}}>{saving ? "Saving..." : "Save"}</Button>
                    </Stack>
                </Box>
            </Fade>
        </Modal>
    );
};

export default EditMCPServerModalNew;
